import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import { ko } from "date-fns/locale/ko";
import "react-day-picker/style.css";
import Swal from "sweetalert2";

import { Site } from "../../api/sites";
import { localApi } from "../../api/http";
import {
    DeploymentTransferCredentials,
    transferDeployment,
} from "../../api/deployments";
import { styles } from "../../styles/PathConverterStyles";
import SshTransferModal from "../deployment/SshTransferModal";
import type {
    SshTerminalSession,
} from "../deployment/SshTerminalWorkspace";
import VirtualCheckList, { VirtualCheckListItem } from "./VirtualCheckList";

interface Props {
    site: Site;
    suspendScrollbars?: boolean;
    onSshSessionOpen: (
        session: Omit<SshTerminalSession, "clientId" | "title">
    ) => void;
}

export type RepoVersionOption = {
    value: string;
    label: string;
    committedAt: string;
};

export type DuplicateFileItem = {
    path: string;
    versions: RepoVersionOption[];
};

type RepoVersionListResponse = {
    vcsType: string;
    versions: RepoVersionOption[];
};

type RepoVersionFileListResponse = {
    vcsType: string;
    files: string[];
    duplicateFiles: DuplicateFileItem[];
};

type ExtractionSaveResponse = {
    fileName: string;
    path: string;
    size: number;
    directoryPath: string;
    historyId: string;
};

type ActiveDatePicker = "start" | "end" | null;
type VersionSortOrder = "latest" | "oldest";
type FileSortOrder = "ascending" | "descending";

type ListHeaderControlsProps<T extends string> = {
    allChecked: boolean;
    partiallyChecked: boolean;
    disabled: boolean;
    onToggleAll: () => void;
    selectionLabel: string;
    sortLabel: string;
    sortValue: T;
    sortOptions: Array<{ value: T; label: string }>;
    onSortChange: (value: T) => void;
};

const DATE_PICKER_START_MONTH = new Date(2000, 0);
const DATE_PICKER_END_MONTH = new Date(new Date().getFullYear() + 5, 11);

const ListHeaderControls = <T extends string>({
    allChecked,
    partiallyChecked,
    disabled,
    onToggleAll,
    selectionLabel,
    sortLabel,
    sortValue,
    sortOptions,
    onSortChange,
}: ListHeaderControlsProps<T>) => {
    const checkboxRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (checkboxRef.current) checkboxRef.current.indeterminate = partiallyChecked;
    }, [partiallyChecked]);

    return (
        <div className="packaging-list-controls">
            <label className="packaging-all-check">
                <input
                    ref={checkboxRef}
                    type="checkbox"
                    aria-label={selectionLabel}
                    checked={allChecked}
                    disabled={disabled}
                    onChange={onToggleAll}
                />
                <span>ALL</span>
            </label>
            <select
                className="packaging-sort-select"
                aria-label={sortLabel}
                value={sortValue}
                disabled={disabled}
                onChange={(event) => onSortChange(event.target.value as T)}
            >
                {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

const toLocalDateText = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const addDays = (date: Date, days: number): Date => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const normalizePath = (path: string): string => path.replace(/\\/g, "/").replace(/^\/+/, "").trim();

const compareText = (left: string, right: string): number =>
    left.localeCompare(right, "ko", { numeric: true, sensitivity: "base" });

const parseDateValue = (value: string): number | null => {
    const normalized = value.trim().replace(" ", "T");
    const time = Date.parse(normalized);
    return Number.isNaN(time) ? null : time;
};

const parseNumericValue = (value: string): number | null => {
    const numeric = Number(value.trim());
    return Number.isFinite(numeric) ? numeric : null;
};

const sortVersionsAscending = (versions: RepoVersionOption[]): RepoVersionOption[] =>
    [...versions].sort((left, right) => {
        const leftTime = parseDateValue(left.committedAt);
        const rightTime = parseDateValue(right.committedAt);
        if (leftTime !== null && rightTime !== null && leftTime !== rightTime) return leftTime - rightTime;

        const leftNumber = parseNumericValue(left.value);
        const rightNumber = parseNumericValue(right.value);
        if (leftNumber !== null && rightNumber !== null && leftNumber !== rightNumber) return leftNumber - rightNumber;

        return compareText(left.label || left.value, right.label || right.value);
    });

const latestVersionValue = (versions: RepoVersionOption[]): string =>
    versions[versions.length - 1]?.value ?? "";

const fileExtensionLabel = (path: string): string => {
    const fileName = normalizePath(path).split("/").pop() ?? "";
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex <= 0 || dotIndex === fileName.length - 1) return "확장자 없음";
    return `.${fileName.slice(dotIndex + 1).toLowerCase()}`;
};

const groupFilesByExtension = (
    files: string[],
    sortOrder: FileSortOrder
): Array<{ extension: string; files: string[] }> => {
    const groups = new Map<string, string[]>();
    const direction = sortOrder === "ascending" ? 1 : -1;

    files.forEach((file) => {
        const extension = fileExtensionLabel(file);
        groups.set(extension, [...(groups.get(extension) ?? []), file]);
    });

    return Array.from(groups.entries())
        .sort(([left], [right]) => {
            if (left === "확장자 없음") return 1;
            if (right === "확장자 없음") return -1;
            return compareText(left, right) * direction;
        })
        .map(([extension, groupFiles]) => ({
            extension,
            files: [...groupFiles].sort((left, right) => compareText(left, right) * direction),
        }));
};

const vcsTypeLabels: Record<string, string> = {
    GIT: "Git",
    SVN: "SVN",
    LOCAL: "로컬(최종 수정 시간 기준)",
    UNKNOWN: "알 수 없음",
};

const PathConverter: React.FC<Props> = ({
    site,
    suspendScrollbars = false,
    onSshSessionOpen,
}) => {
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [activeDatePicker, setActiveDatePicker] = useState<ActiveDatePicker>(null);
    const dateRangeRef = useRef<HTMLDivElement>(null);

    const [versionOptions, setVersionOptions] = useState<RepoVersionOption[]>([]);
    const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
    const [isLoadingVersions, setIsLoadingVersions] = useState(false);
    const [versionSortOrder, setVersionSortOrder] = useState<VersionSortOrder>("latest");

    const [versionFiles, setVersionFiles] = useState<string[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [fileSortOrder, setFileSortOrder] = useState<FileSortOrder>("ascending");

    const [duplicateFiles, setDuplicateFiles] = useState<DuplicateFileItem[]>([]);
    const [duplicateFileVersionMap, setDuplicateFileVersionMap] = useState<Record<string, string>>({});
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

    const [vcsType, setVcsType] = useState("");
    const [isExtracting, setIsExtracting] = useState(false);
    const [pendingServerTransfer, setPendingServerTransfer] = useState(false);
    const [pendingTransferCredentials, setPendingTransferCredentials] =
        useState<DeploymentTransferCredentials | null>(null);
    const [isSshTransferModalOpen, setIsSshTransferModalOpen] = useState(false);
    const [transferSite, setTransferSite] = useState<Site>(site);

    const isLocalMode = vcsType === "LOCAL";
    const hasFileQuery = isLocalMode ? Boolean(startDate && endDate) : selectedVersions.length > 0;
    const displayVcsType = vcsTypeLabels[vcsType] ?? vcsType;

    const selectedVersionSet = useMemo(() => new Set(selectedVersions), [selectedVersions]);
    const selectedFileSet = useMemo(() => new Set(selectedFiles), [selectedFiles]);
    const selectedVersionCount = useMemo(
        () => versionOptions.filter((option) => selectedVersionSet.has(option.value)).length,
        [selectedVersionSet, versionOptions]
    );
    const selectedFileCount = useMemo(
        () => versionFiles.filter((file) => selectedFileSet.has(file)).length,
        [selectedFileSet, versionFiles]
    );
    const allVersionsChecked = versionOptions.length > 0 && selectedVersionCount === versionOptions.length;
    const someVersionsChecked = selectedVersionCount > 0 && !allVersionsChecked;
    const allFilesChecked = versionFiles.length > 0 && selectedFileCount === versionFiles.length;
    const someFilesChecked = selectedFileCount > 0 && !allFilesChecked;
    const duplicatePathSet = useMemo(() => new Set(duplicateFiles.map((item) => item.path)), [duplicateFiles]);
    const selectedDuplicateFiles = useMemo(
        () => duplicateFiles.filter((item) => selectedFileSet.has(item.path)),
        [duplicateFiles, selectedFileSet]
    );

    useEffect(() => {
        setTransferSite(site);
        setIsSshTransferModalOpen(false);
        setPendingServerTransfer(false);
        setPendingTransferCredentials(null);
    }, [site]);

    useEffect(() => {
        if (!activeDatePicker) return;

        const handleMouseDown = (event: MouseEvent) => {
            if (!dateRangeRef.current?.contains(event.target as Node)) {
                setActiveDatePicker(null);
            }
        };

        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [activeDatePicker]);

    useEffect(() => {
        if (!site.localPath || !startDate || !endDate) {
            setVersionOptions([]);
            setSelectedVersions([]);
            setVersionFiles([]);
            setSelectedFiles([]);
            setDuplicateFiles([]);
            setDuplicateFileVersionMap({});
            setIsDuplicateModalOpen(false);
            setVcsType("");
            setIsLoadingVersions(false);
            return;
        }

        let cancelled = false;
        setIsLoadingVersions(true);
        setSelectedVersions([]);
        setVersionFiles([]);
        setSelectedFiles([]);
        setDuplicateFiles([]);
        setDuplicateFileVersionMap({});
        setIsDuplicateModalOpen(false);

        localApi
            .post<RepoVersionListResponse>("/api/git/versions", {
                localPath: site.localPath,
                since: toLocalDateText(startDate),
                until: toLocalDateText(endDate),
            })
            .then((res) => {
                if (cancelled) return;
                setVersionOptions(sortVersionsAscending(res.data.versions ?? []));
                setVcsType(res.data.vcsType ?? "");
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Failed to load versions:", err);
                setVersionOptions([]);
                setVcsType("");
                Swal.fire({
                    icon: "error",
                    title: "버전 조회 실패",
                    text: "저장소 경로와 권한을 확인해 주세요.",
                }).then();
            })
            .finally(() => {
                if (!cancelled) setIsLoadingVersions(false);
            });

        return () => {
            cancelled = true;
        };
    }, [site.localPath, startDate, endDate]);

    useEffect(() => {
        const shouldLoadLocalFiles = Boolean(site.localPath && startDate && endDate && isLocalMode);
        const shouldLoadVersionFiles = Boolean(site.localPath && !isLocalMode && selectedVersions.length > 0);

        if (!shouldLoadLocalFiles && !shouldLoadVersionFiles) {
            setVersionFiles([]);
            setSelectedFiles([]);
            setDuplicateFiles([]);
            setDuplicateFileVersionMap({});
            setIsDuplicateModalOpen(false);
            setIsLoadingFiles(false);
            return;
        }

        let cancelled = false;
        setIsLoadingFiles(true);

        localApi
            .post<RepoVersionFileListResponse>("/api/git/version-files", {
                localPath: site.localPath,
                selectedVersions,
                since: startDate ? toLocalDateText(startDate) : null,
                until: endDate ? toLocalDateText(endDate) : null,
            })
            .then((res) => {
                if (cancelled) return;

                const files = (res.data.files ?? []).map(normalizePath);
                const duplicates = (res.data.duplicateFiles ?? []).map((item) => ({
                    path: normalizePath(item.path),
                    versions: sortVersionsAscending(item.versions ?? []),
                }));

                setVersionFiles(files);
                setSelectedFiles(files);
                setDuplicateFiles(duplicates);
                setDuplicateFileVersionMap((prev) => {
                    const next: Record<string, string> = {};
                    duplicates.forEach((item) => {
                        const available = item.versions.map((version) => version.value);
                        const prevValue = prev[item.path];
                        next[item.path] = available.includes(prevValue) ? prevValue : latestVersionValue(item.versions);
                    });
                    return next;
                });
                setVcsType((prev) => res.data.vcsType ?? prev);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Failed to load changed files:", err);
                setVersionFiles([]);
                setSelectedFiles([]);
                setDuplicateFiles([]);
                setDuplicateFileVersionMap({});
                setIsDuplicateModalOpen(false);
                Swal.fire({
                    icon: "error",
                    title: "파일 조회 실패",
                    text: "변경 파일 목록을 불러오지 못했습니다.",
                }).then();
            })
            .finally(() => {
                if (!cancelled) setIsLoadingFiles(false);
            });

        return () => {
            cancelled = true;
        };
    }, [site.localPath, selectedVersions, startDate, endDate, isLocalMode]);

    useEffect(() => {
        if (!isDuplicateModalOpen) return;

        const previousOverflow = document.body.style.overflow;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsDuplicateModalOpen(false);
            }
        };

        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isDuplicateModalOpen]);

    useEffect(() => {
        if (!isExtracting) return;

        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, [isExtracting]);

    useEffect(() => {
        if (isDuplicateModalOpen && selectedDuplicateFiles.length === 0) {
            setIsDuplicateModalOpen(false);
        }
    }, [isDuplicateModalOpen, selectedDuplicateFiles.length]);

    const resolveTargetOs = (): "WINDOWS" | "MAC" | "LINUX" | null => {
        const ua = navigator.userAgent.toLowerCase();
        if (ua.includes("windows")) return "WINDOWS";
        if (ua.includes("mac")) return "MAC";
        if (ua.includes("linux")) return "LINUX";
        return null;
    };

    const requiresJvmCompilation = (): boolean =>
        selectedFiles.some((file) => file.endsWith(".java") || file.endsWith(".kt"));

    const confirmMissingJdkPath = async (): Promise<boolean> => {
        if (!requiresJvmCompilation() || site.jdkPath?.trim()) return true;

        const result = await Swal.fire({
            icon: "warning",
            title: "JDK 경로 미설정",
            text: "Java/Kotlin 소스가 포함되어 있어 class 생성이 실패할 수 있습니다. 계속 진행할까요?",
            showCancelButton: true,
            confirmButtonText: "계속 진행",
            cancelButtonText: "취소",
        });

        return result.isConfirmed;
    };

    const validateInputs = async (options?: { includeDuplicateVersions?: boolean }): Promise<boolean> => {
        const includeDuplicateVersions = options?.includeDuplicateVersions ?? true;

        if (!startDate || !endDate) {
            await Swal.fire({
                icon: "warning",
                title: "날짜 선택 필요",
                text: "시작일과 종료일을 먼저 선택해 주세요.",
            });
            return false;
        }

        if (isLoadingVersions || isLoadingFiles) {
            await Swal.fire({
                icon: "info",
                title: "조회 진행 중",
                text: "목록 조회가 끝난 뒤 다시 시도해 주세요.",
            });
            return false;
        }

        if (!isLocalMode && versionOptions.length === 0) {
            await Swal.fire({
                icon: "warning",
                title: "버전 없음",
                text: "선택한 날짜 범위에 해당하는 버전이 없습니다.",
            });
            return false;
        }

        if (!isLocalMode && selectedVersions.length === 0) {
            await Swal.fire({
                icon: "warning",
                title: "버전 선택 필요",
                text: "최소 1개 이상의 버전을 선택해 주세요.",
            });
            return false;
        }

        if (selectedFiles.length === 0) {
            await Swal.fire({
                icon: "warning",
                title: "파일 선택 필요",
                text: "최소 1개 이상의 파일을 선택해 주세요.",
            });
            return false;
        }

        if (includeDuplicateVersions) {
            const missingDuplicateVersion = selectedDuplicateFiles.find((item) => !duplicateFileVersionMap[item.path]);
            if (missingDuplicateVersion) {
                await Swal.fire({
                    icon: "warning",
                    title: "중복 파일 버전 선택 필요",
                    text: `중복 파일의 버전을 선택해 주세요: ${missingDuplicateVersion.path}`,
                });
                return false;
            }
        }

        return true;
    };

    const toggleVersion = useCallback((value: string) => {
        setSelectedVersions((prev) =>
            prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
        );
    }, []);

    const toggleFile = useCallback((value: string) => {
        setSelectedFiles((prev) =>
            prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
        );
    }, []);

    const performExtraction = async (transferCredentials?: DeploymentTransferCredentials) => {
        const targetOs = resolveTargetOs();
        if (!targetOs) {
            await Swal.fire({
                icon: "error",
                title: "지원하지 않는 OS",
                text: "클라이언트 OS를 감지하지 못했습니다.",
            });
            return;
        }

        setIsExtracting(true);
        Swal.fire({
            title: transferCredentials ? "전송 패키지 생성 중" : "패키지 생성 중",
            text: transferCredentials
                ? "선택한 파일로 패키지를 생성한 뒤 서버에 전송합니다."
                : isLocalMode
                  ? "선택한 파일 기준으로 배포 패키지를 생성하고 있습니다."
                  : "선택한 버전과 파일 기준으로 배포 패키지를 생성하고 있습니다.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
                popup: "dp-extraction-alert",
            },
            didOpen: () => Swal.showLoading(),
        });

        try {
            const selectedDuplicateVersionMap = Object.fromEntries(
                selectedDuplicateFiles
                    .map((item) => [item.path, duplicateFileVersionMap[item.path]])
                    .filter((entry): entry is [string, string] => Boolean(entry[1]))
            );

            const response = await localApi.post<ExtractionSaveResponse>(
                "/api/git/extraction/save",
                {
                    siteId: site.id,
                    siteName: site.text,
                    since: startDate ? toLocalDateText(startDate) : null,
                    until: endDate ? toLocalDateText(endDate) : null,
                    localPath: site.localPath,
                    homePath: site.homePath,
                    jdkPath: site.jdkPath,
                    fileStatusType: isLocalMode ? "STATUS" : "DIFF",
                    targetOs,
                    selectedVersions,
                    selectedFiles: selectedFiles.map(normalizePath),
                    duplicateFileVersionMap: selectedDuplicateVersionMap,
                },
                { timeout: 1800000 }
            );

            if (transferCredentials) {
                Swal.update({
                    title: "서버 전송 중",
                    text: `${response.data.fileName}을 ${transferCredentials.sshHost ?? transferSite.sshHost}에 전송하고 있습니다.`,
                });
                Swal.showLoading();

                const transferred = await transferDeployment(
                    response.data.historyId,
                    transferCredentials
                );
                Swal.close();
                onSshSessionOpen({
                    historyId: response.data.historyId,
                    site: {
                        ...transferSite,
                        sshHost: transferCredentials.sshHost ?? transferSite.sshHost,
                        sshPort: transferCredentials.sshPort ?? transferSite.sshPort,
                        sshUsername: transferCredentials.sshUsername ?? transferSite.sshUsername,
                        sshAuthType: transferCredentials.sshAuthType ?? transferSite.sshAuthType,
                        sshPrivateKeyPath:
                            transferCredentials.sshPrivateKeyPath ?? transferSite.sshPrivateKeyPath,
                        strictHostKeyChecking:
                            transferCredentials.strictHostKeyChecking ??
                            transferSite.strictHostKeyChecking,
                    },
                    credentials: transferCredentials,
                    workingDirectory:
                        transferred.remotePath ?? transferCredentials.remoteUploadPath ?? "deploykit",
                });
                return;
            }

            await Swal.fire({
                icon: "success",
                title: "패키지 생성 완료",
                text: `${response.data.fileName}을 바탕화면에 생성했습니다. 저장 폴더를 열었습니다.`,
                confirmButtonText: "확인",
            });
        } catch (err) {
            console.error("Extraction error:", err);
            const responseData = (err as {
                response?: { data?: { message?: string; detail?: string } };
            })?.response?.data;
            const serverMessage = responseData?.message || responseData?.detail;
            await Swal.fire({
                icon: "error",
                title: transferCredentials ? "서버 전송 실패" : "패키지 생성 실패",
                text:
                    serverMessage ||
                    (transferCredentials
                        ? "패키지는 바탕화면과 배포 이력에 남아 있습니다. 접속 설정을 확인한 뒤 이력에서 재전송해 주세요."
                        : "서버 로그와 입력 조건을 확인해 주세요."),
            });
        } finally {
            setIsExtracting(false);
        }
    };

    const closeDuplicateModal = () => {
        if (isExtracting) return;
        setIsDuplicateModalOpen(false);
        setPendingServerTransfer(false);
        setPendingTransferCredentials(null);
    };

    const handleExtractClick = async (serverTransfer: boolean) => {
        if (serverTransfer) {
            setIsSshTransferModalOpen(true);
            return;
        }

        if (!(await validateInputs({ includeDuplicateVersions: false }))) return;
        if (!(await confirmMissingJdkPath())) return;

        if (selectedDuplicateFiles.length > 0) {
            setPendingServerTransfer(false);
            setPendingTransferCredentials(null);
            setIsDuplicateModalOpen(true);
            return;
        }

        await performExtraction();
    };

    const handleDuplicateConfirm = async () => {
        if (!(await validateInputs())) return;
        if (!(await confirmMissingJdkPath())) return;

        const transferCredentials = pendingServerTransfer
            ? pendingTransferCredentials ?? undefined
            : undefined;
        setIsDuplicateModalOpen(false);
        setPendingServerTransfer(false);
        setPendingTransferCredentials(null);
        await performExtraction(transferCredentials);
    };

    const handleTransferSubmit = async (request: DeploymentTransferCredentials) => {
        if (request.saveConnection) {
            setTransferSite((prev) => ({
                ...prev,
                sshHost: request.sshHost,
                sshPort: request.sshPort,
                sshUsername: request.sshUsername,
                sshAuthType: request.sshAuthType,
                sshPrivateKeyPath: request.sshPrivateKeyPath,
                remoteUploadPath: request.remoteUploadPath,
                strictHostKeyChecking: request.strictHostKeyChecking,
            }));
        }

        if (!(await validateInputs({ includeDuplicateVersions: false }))) return;
        if (!(await confirmMissingJdkPath())) return;

        if (selectedDuplicateFiles.length > 0) {
            setPendingServerTransfer(true);
            setPendingTransferCredentials(request);
            setIsDuplicateModalOpen(true);
            return;
        }

        setPendingServerTransfer(false);
        setPendingTransferCredentials(null);
        await performExtraction(request);
    };

    const selectDateRange = (from: Date | null, to: Date | null, closePicker = false) => {
        setStartDate(from);
        setEndDate(to);
        if (closePicker) setActiveDatePicker(null);
    };

    const selectRecentDays = (days: number) => {
        const today = new Date();
        const from = addDays(today, -(days - 1));
        selectDateRange(from, today, true);
    };

    const selectBoundaryDate = (date: Date | undefined) => {
        if (!date || !activeDatePicker) return;

        if (activeDatePicker === "start") {
            setStartDate(date);
            if (endDate && date > endDate) setEndDate(null);
            setActiveDatePicker("end");
            return;
        }

        if (startDate && date < startDate) {
            setStartDate(date);
            setEndDate(startDate);
        } else {
            setEndDate(date);
        }
        setActiveDatePicker(null);
    };

    const activePickerDate = activeDatePicker === "start" ? startDate : endDate;
    const activePickerDefaultMonth = activePickerDate ?? startDate ?? endDate ?? new Date();
    const sortedVersionOptions = useMemo(() => {
        const ascending = sortVersionsAscending(versionOptions);
        return versionSortOrder === "latest" ? ascending.reverse() : ascending;
    }, [versionOptions, versionSortOrder]);
    const versionCheckItems = useMemo<VirtualCheckListItem[]>(
        () =>
            sortedVersionOptions.map((option) => ({
                key: option.value,
                label: option.label,
                checked: selectedVersionSet.has(option.value),
                onToggle: () => toggleVersion(option.value),
            })),
        [selectedVersionSet, sortedVersionOptions, toggleVersion]
    );
    const fileCheckItems = useMemo<VirtualCheckListItem[]>(
        () => {
            const items: VirtualCheckListItem[] = [];

            groupFilesByExtension(versionFiles, fileSortOrder).forEach((group) => {
                items.push({
                    type: "header",
                    key: `extension-${group.extension}`,
                    label: group.extension,
                    count: group.files.length,
                });

                group.files.forEach((file) => {
                    items.push({
                        key: file,
                        label: file,
                        checked: selectedFileSet.has(file),
                        badge: duplicatePathSet.has(file) ? "중복" : undefined,
                        onToggle: () => toggleFile(file),
                    });
                });
            });

            return items;
        },
        [duplicatePathSet, fileSortOrder, selectedFileSet, toggleFile, versionFiles]
    );
    const versionListResetKey = useMemo(
        () => `${versionSortOrder}:${sortedVersionOptions.map((option) => option.value).join("|")}`,
        [sortedVersionOptions, versionSortOrder]
    );
    const fileListResetKey = useMemo(
        () => `${fileSortOrder}:${versionFiles.join("|")}`,
        [fileSortOrder, versionFiles]
    );

    const suspendListScroll = isDuplicateModalOpen || isExtracting || suspendScrollbars;
    const checkListBoxStyle = suspendListScroll
        ? { ...styles.checkListBox, overflowY: "hidden" as const }
        : styles.checkListBox;
    const renderEmptyState = (title: string, description: string) => (
        <div style={styles.emptyState}>
            <div style={styles.emptyStateTitle}>{title}</div>
            <div style={styles.emptyStateDescription}>{description}</div>
        </div>
    );

    return (
        <div style={styles.customCardSingle}>
            <div style={styles.mainCard}>
                <div className="packaging-date-toolbar" style={styles.controlsRow}>
                    <div className="date-range-control" ref={dateRangeRef}>
                        <div className="date-range-fields">
                            <button
                                type="button"
                                className={`date-range-field${activeDatePicker === "start" ? " active" : ""}`}
                                aria-expanded={activeDatePicker === "start"}
                                onClick={() => setActiveDatePicker((prev) => (prev === "start" ? null : "start"))}
                            >
                                <span>시작일</span>
                                <strong>{startDate ? toLocalDateText(startDate) : "날짜 선택"}</strong>
                            </button>
                            <span className="date-range-field-separator">~</span>
                            <button
                                type="button"
                                className={`date-range-field${activeDatePicker === "end" ? " active" : ""}`}
                                aria-expanded={activeDatePicker === "end"}
                                onClick={() => setActiveDatePicker((prev) => (prev === "end" ? null : "end"))}
                            >
                                <span>종료일</span>
                                <strong>{endDate ? toLocalDateText(endDate) : "날짜 선택"}</strong>
                            </button>
                        </div>

                        {activeDatePicker && (
                            <div className="date-range-popover" role="dialog" aria-label={`${activeDatePicker === "start" ? "시작일" : "종료일"} 선택`}>
                                <div className="date-range-popover-title">
                                    {activeDatePicker === "start" ? "시작일 선택" : "종료일 선택"}
                                </div>
                                <DayPicker
                                    mode="single"
                                    locale={ko}
                                    weekStartsOn={1}
                                    captionLayout="dropdown"
                                    startMonth={DATE_PICKER_START_MONTH}
                                    endMonth={DATE_PICKER_END_MONTH}
                                    reverseYears
                                    defaultMonth={activePickerDefaultMonth}
                                    selected={activePickerDate ?? undefined}
                                    onSelect={selectBoundaryDate}
                                    formatters={{
                                        formatMonthDropdown: (date) => `${date.getMonth() + 1}월`,
                                        formatYearDropdown: (date) => `${date.getFullYear()}년`,
                                    }}
                                    labels={{
                                        labelMonthDropdown: () => "월 선택",
                                        labelYearDropdown: () => "년도 선택",
                                    }}
                                />
                                <div className="date-range-actions">
                                    <button type="button" onClick={() => selectRecentDays(1)}>
                                        오늘
                                    </button>
                                    <button type="button" onClick={() => selectRecentDays(7)}>
                                        최근 7일
                                    </button>
                                    <button type="button" onClick={() => selectRecentDays(30)}>
                                        최근 30일
                                    </button>
                                    <button type="button" onClick={() => selectDateRange(null, null)}>
                                        초기화
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div style={styles.contentLayout}>
                    <div
                        className={`packaging-selection-grid${isLocalMode ? " is-single" : ""}`}
                        style={{
                            ...styles.selectionGrid,
                            gridTemplateColumns: isLocalMode ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
                        }}
                    >
                        {!isLocalMode && (
                            <section className="packaging-selection-panel" style={styles.selectionPanel}>
                                <div style={styles.panelHeader}>
                                    <label style={styles.panelTitle}>버전 선택</label>
                                    <ListHeaderControls
                                        allChecked={allVersionsChecked}
                                        partiallyChecked={someVersionsChecked}
                                        disabled={isLoadingVersions || versionOptions.length === 0}
                                        onToggleAll={() =>
                                            setSelectedVersions(
                                                allVersionsChecked
                                                    ? []
                                                    : versionOptions.map((item) => item.value)
                                            )
                                        }
                                        selectionLabel="버전 전체 선택"
                                        sortLabel="버전 정렬"
                                        sortValue={versionSortOrder}
                                        sortOptions={[
                                            { value: "latest", label: "최신순" },
                                            { value: "oldest", label: "오래된순" },
                                        ]}
                                        onSortChange={setVersionSortOrder}
                                    />
                                </div>

                                {isLoadingVersions || versionCheckItems.length === 0 ? (
                                    <div style={checkListBoxStyle}>
                                        {isLoadingVersions &&
                                            renderEmptyState("버전 목록 조회 중", "선택한 기간의 Git commit 또는 SVN revision을 불러오고 있습니다.")}
                                        {!isLoadingVersions && (!startDate || !endDate) &&
                                            renderEmptyState("날짜를 선택해 주세요", "시작일과 종료일을 지정하면 변경 이력이 있는 버전을 조회합니다.")}
                                        {!isLoadingVersions && startDate && endDate && (
                                            renderEmptyState("조회된 버전이 없습니다", "기간을 넓히거나 선택한 프로젝트 경로를 확인해 주세요.")
                                        )}
                                    </div>
                                ) : (
                                    <VirtualCheckList
                                        items={versionCheckItems}
                                        listStyle={styles.checkListBox}
                                        rowStyle={styles.checkRow}
                                        activeRowStyle={styles.checkRowActive}
                                        inputStyle={styles.checkInput}
                                        textStyle={styles.checkText}
                                        suspendScroll={suspendListScroll}
                                        resetKey={versionListResetKey}
                                    />
                                )}
                            </section>
                        )}

                        <section className="packaging-selection-panel" style={styles.selectionPanel}>
                            <div style={styles.panelHeader}>
                                <label style={styles.panelTitle}>파일 선택</label>
                                <ListHeaderControls
                                    allChecked={allFilesChecked}
                                    partiallyChecked={someFilesChecked}
                                    disabled={isLoadingFiles || versionFiles.length === 0}
                                    onToggleAll={() =>
                                        setSelectedFiles(allFilesChecked ? [] : versionFiles)
                                    }
                                    selectionLabel="파일 전체 선택"
                                    sortLabel="파일 정렬"
                                    sortValue={fileSortOrder}
                                    sortOptions={[
                                        { value: "ascending", label: "경로 오름차순" },
                                        { value: "descending", label: "경로 내림차순" },
                                    ]}
                                    onSortChange={setFileSortOrder}
                                />
                            </div>

                            {!hasFileQuery || isLoadingFiles || fileCheckItems.length === 0 ? (
                                <div style={checkListBoxStyle}>
                                    {!isLocalMode && selectedVersions.length === 0 &&
                                        renderEmptyState("버전을 선택해 주세요", "선택한 버전에 포함된 변경 파일을 이 영역에 표시합니다.")}
                                    {hasFileQuery && isLoadingFiles && (
                                        renderEmptyState(
                                            "파일 목록 조회 중",
                                            isLocalMode
                                                ? "선택한 날짜 범위의 수정 파일을 불러오고 있습니다."
                                                : "선택한 버전의 변경 파일을 불러오고 있습니다."
                                        )
                                    )}
                                    {hasFileQuery && !isLoadingFiles && versionFiles.length === 0 && (
                                        renderEmptyState(
                                            "조회된 파일이 없습니다",
                                            isLocalMode
                                                ? "기간을 넓히거나 로컬 프로젝트 경로를 확인해 주세요."
                                                : "다른 버전을 선택하거나 저장소 상태를 확인해 주세요."
                                        )
                                    )}
                                </div>
                            ) : (
                                <VirtualCheckList
                                    items={fileCheckItems}
                                    listStyle={styles.checkListBox}
                                    rowStyle={styles.checkRow}
                                    activeRowStyle={styles.checkRowActive}
                                    inputStyle={styles.checkInput}
                                    textStyle={styles.checkText}
                                    badgeStyle={styles.duplicateBadge}
                                    headerStyle={styles.checkGroupHeader}
                                    headerCountStyle={styles.checkGroupCount}
                                    suspendScroll={suspendListScroll}
                                    resetKey={fileListResetKey}
                                />
                            )}
                        </section>
                    </div>
                </div>

                {vcsType && (
                    <div style={styles.versionTypeText}>
                        감지된 대상 유형: <strong>{displayVcsType}</strong>
                    </div>
                )}

                <div style={styles.extractActions}>
                    <button
                        onClick={() => handleExtractClick(false)}
                        className="packaging-action-button is-primary"
                        style={styles.extractBtn}
                        disabled={isExtracting || isLoadingVersions || isLoadingFiles}
                    >
                        {isExtracting ? "처리 중..." : "패키지 추출"}
                    </button>
                    <button
                        onClick={() => handleExtractClick(true)}
                        className="packaging-action-button is-secondary"
                        style={styles.transferBtn}
                        disabled={isExtracting}
                    >
                        서버 전송
                    </button>
                </div>
            </div>

            <SshTransferModal
                show={isSshTransferModalOpen}
                site={transferSite}
                onClose={() => setIsSshTransferModalOpen(false)}
                onSubmit={handleTransferSubmit}
            />

            {isDuplicateModalOpen && (
                <div style={styles.duplicateModalOverlay} onClick={closeDuplicateModal}>
                    <div style={styles.duplicateModalDialog} onClick={(event) => event.stopPropagation()}>
                        <div style={styles.panelHeader}>
                            <div>
                                <div style={styles.panelTitle}>중복 파일 버전 선택</div>
                                <div style={styles.panelSubTitle}>
                                    같은 파일 경로가 여러 버전에 포함되어 있어 사용할 버전을 선택해야 합니다.
                                </div>
                            </div>
                            <div style={styles.duplicateModalHeaderActions}>
                                <span style={styles.summaryChip}>총 {selectedDuplicateFiles.length}건</span>
                                <button type="button" style={styles.duplicateCloseBtn} onClick={closeDuplicateModal}>
                                    닫기
                                </button>
                            </div>
                        </div>

                        {selectedDuplicateFiles.length === 0 && (
                            <div style={styles.emptyText}>선택한 파일에는 중복 항목이 없습니다.</div>
                        )}

                        {selectedDuplicateFiles.length > 0 && (
                            <div style={styles.duplicateListBox}>
                                {selectedDuplicateFiles.map((item, idx) => (
                                    <div key={item.path} style={styles.duplicateRow}>
                                        <div style={styles.duplicatePathWrap}>
                                            <span style={styles.duplicateIndex}>{idx + 1}</span>
                                            <span style={styles.duplicatePath}>{item.path}</span>
                                        </div>
                                        <select
                                            style={styles.duplicateSelect}
                                            value={duplicateFileVersionMap[item.path] ?? ""}
                                            onChange={(event) =>
                                                setDuplicateFileVersionMap((prev) => ({
                                                    ...prev,
                                                    [item.path]: event.target.value,
                                                }))
                                            }
                                        >
                                            {item.versions.length === 0 && <option value="">선택 가능한 버전 없음</option>}
                                            {item.versions.map((version) => (
                                                <option key={`${item.path}-${version.value}`} value={version.value}>
                                                    {version.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={styles.modalFooter}>
                            <button type="button" style={styles.modalSecondaryButton} onClick={closeDuplicateModal}>
                                취소
                            </button>
                            <button
                                type="button"
                                style={styles.modalPrimaryButton}
                                onClick={handleDuplicateConfirm}
                                disabled={isExtracting}
                            >
                                {pendingServerTransfer ? "이 버전으로 전송" : "이 버전으로 추출"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PathConverter;
